import React, { useEffect, useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { adminAPI } from '../services/api';

const PLATFORM_COLORS = {
  ios: '#6366f1',
  android: '#22c55e',
  web: '#f59e0b',
  unknown: '#9ca3af',
};

function PlatformBadge({ platform }) {
  const label = platform ? String(platform).toUpperCase() : 'UNKNOWN';
  const color = PLATFORM_COLORS[platform] || PLATFORM_COLORS.unknown;
  return (
    <span
      className="px-2 py-1 rounded-full text-xs font-semibold text-white"
      style={{ backgroundColor: color }}
    >
      {label}
    </span>
  );
}

function PlatformSection({ title, platforms, total }) {
  const chartData = (platforms || []).map((row) => ({
    name: row.platform,
    value: row.count,
  }));

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-1">{title}</h2>
      <p className="text-sm text-gray-500 mb-4">{total ?? 0} total accounts</p>
      {chartData.length === 0 ? (
        <p className="text-gray-500 text-sm">No platform data yet.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) =>
                    `${String(name).toUpperCase()} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={PLATFORM_COLORS[entry.name] || PLATFORM_COLORS.unknown}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {chartData.map((row) => (
              <div
                key={row.name}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
              >
                <PlatformBadge platform={row.name} />
                <span className="font-semibold text-gray-800">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RegionSection({ title, rows, tagged, untagged, dimension }) {
  const chartData = (rows || []).slice(0, 12).map((row) => ({
    name: row.label,
    count: row.count,
  }));

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-1">{title}</h2>
      <p className="text-sm text-gray-500 mb-4">
        {tagged ?? 0} with {dimension.toLowerCase()} tagged · {untagged ?? 0} without {dimension.toLowerCase()} data
      </p>
      {chartData.length === 0 ? (
        <p className="text-gray-500 text-sm">
          No {dimension.toLowerCase()} data yet. Regions are tagged when users update location in the app.
        </p>
      ) : (
        <>
          <div className="h-72 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#ec4899" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">{dimension}</th>
                {dimension === 'City' && <th className="py-2">State</th>}
                <th className="py-2 text-right">Users</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((row) => (
                <tr key={dimension === 'City' ? `${row.city}-${row.state}` : row.name} className="border-b border-gray-100">
                  <td className="py-2 font-medium">{dimension === 'City' ? row.city : row.name}</td>
                  {dimension === 'City' && <td className="py-2 text-gray-600">{row.state || '—'}</td>}
                  <td className="py-2 text-right font-semibold">{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function RegionalInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dimension, setDimension] = useState('city');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getDemographicsAnalytics({ cityLimit: 50, regionLimit: 50 });
        setData(response.data.demographics);
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load regional insights');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="text-center py-16 text-gray-500">Loading regional insights...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  const active = data?.activeUsers || {};
  const deleted = data?.deletedAccounts || {};

  return (
    <div>
      <h1 className="text-4xl font-bold text-gray-900 mb-2">Regional Insights</h1>
      <p className="text-gray-600 mb-8 max-w-3xl">
        Explore users by city, state, country, or device. Regional data is reverse-geocoded when users
        update location — older accounts may show as untagged until they open the app again.
      </p>

      {data?.notes?.length > 0 && (
        <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-900">
          <ul className="list-disc list-inside space-y-1">
            {data.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {[
          { id: 'city', label: 'By City' },
          { id: 'state', label: 'By State' },
          { id: 'country', label: 'By Country' },
          { id: 'device', label: 'By Device' },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setDimension(item.id)}
            className={`px-5 py-2 rounded-lg font-semibold transition ${dimension === item.id ? 'bg-white text-pink-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {dimension === 'device' ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <PlatformSection title="Active users — device" platforms={active.platforms} total={active.total} />
          <PlatformSection title="Deleted accounts — device" platforms={deleted.platforms} total={deleted.total} />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <RegionSection
            title={`Active users — top ${dimension === 'city' ? 'cities' : dimension === 'state' ? 'states' : 'countries'}`}
            rows={dimension === 'city' ? active.cities : dimension === 'state' ? active.states : active.countries}
            tagged={dimension === 'city' ? active.withCityTagged : dimension === 'state' ? active.withStateTagged : active.withCountryTagged}
            untagged={dimension === 'city' ? active.withoutCityTagged : dimension === 'state' ? active.withoutStateTagged : active.withoutCountryTagged}
            dimension={dimension === 'city' ? 'City' : dimension === 'state' ? 'State' : 'Country'}
          />
          <RegionSection
            title={`Deleted accounts — top ${dimension === 'city' ? 'cities' : dimension === 'state' ? 'states' : 'countries'}`}
            rows={dimension === 'city' ? deleted.cities : dimension === 'state' ? deleted.states : deleted.countries}
            tagged={dimension === 'city' ? deleted.withCityTagged : dimension === 'state' ? deleted.withStateTagged : deleted.withCountryTagged}
            untagged={dimension === 'city' ? deleted.withoutCityTagged : dimension === 'state' ? deleted.withoutStateTagged : deleted.withoutCountryTagged}
            dimension={dimension === 'city' ? 'City' : dimension === 'state' ? 'State' : 'Country'}
          />
        </div>
      )}
    </div>
  );
}

export default RegionalInsights;
